import { useEffect, useState } from "react";
import {
  DashboardGraph,
  DashboardGraphCompare,
} from "../../../services/ApiUniformSurveyGraph";
import "../../../scss/backoffice.scss";
import { Sidemenu } from "../Sidemenu";
import { Topbar } from "../Topbar";
import { Footer } from "../Footers";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface DashboardData {
  percentTotalSubmittedStr: string;
  acceptDeclineAllItem: {
    accept: number;
    decline: number;
    acceptMan: number;
    declineMan: number;
    percentAccept: number;
    percentDecline: number;
  };
  dashBoardSummaryBySites: Array<{
    siteId: string;
    total: number;
    submitted: number;
    pending: number;
  }>;
}

const YEARS = Array.from({ length: 3 }, (_, i) =>
  (new Date().getFullYear() - i).toString()
).reverse();

const COLORS = {
  primary: "#1a73e8",
  secondary: "#ff6b6b",
  tertiary: "#4ecdc4",
  success: "#2ecc71",
  warning: "#f1c40f",
};

const CHART_COLORS = ["#1a73e8", "#ff6b6b", "#4ecdc4", "#2ecc71"];

const LoadingOverlay = () => (
  <div className="pageLoder">
    <div className="loader-box">
      <div className="spinner"></div>
    </div>
  </div>
);

export const UniformDashboard = () => {
  const [data, setData] = useState<{
    dashboardData: DashboardData | null;
    dashboardDataCompareBKK: any;
    dashboardDataCompareLpn: any;
  }>({
    dashboardData: null,
    dashboardDataCompareBKK: null,
    dashboardDataCompareLpn: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(YEARS[2]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [dashboardResponse, bkkResponse, lpnResponse] = await Promise.all([
          DashboardGraph(selectedYear),
          DashboardGraphCompare("BKK", selectedYear),
          DashboardGraphCompare("LPN", selectedYear),
        ]);

        setData({
          dashboardData: dashboardResponse?.data || null,
          dashboardDataCompareBKK: bkkResponse?.data || null,
          dashboardDataCompareLpn: lpnResponse?.data || null,
        });
      } catch (error) {
        // Silent fail
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedYear]);

  const formatNumber = (num: number) => num.toLocaleString();

  const preparePieData = (data: DashboardData["acceptDeclineAllItem"]) => {
    if (!data) return [];
    return [
      { name: "Accept", value: data.percentAccept },
      { name: "Decline", value: data.percentDecline },
    ];
  };

  return (
    <div className="container-fluid master-layout bg-light">
      <Sidemenu />
      <div className="main-container p-4">
        <Topbar />

        <div className="dashboard-grid mt-4">
          <div className="d-flex justify-content-end">
            <div className="d-flex align-items-center mb-3">
              <span className="me-2">Year:</span>
              <select
                className="form-select"
                style={{ width: "auto" }}
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {YEARS.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isLoading ? (
            <LoadingOverlay />
          ) : (
            <>
              {/* Summary Stats */}
              {data.dashboardData && (
                <div className="row g-4 mb-4">
                  <div className="col-md-3">
                    <div className="stat-card bg-white p-4 rounded shadow-sm">
                      <h6 className="text-muted">Accepted Item</h6>
                      <h3>
                        {formatNumber(data.dashboardData?.acceptDeclineAllItem?.accept || 0)} Unit
                      </h3>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="stat-card bg-white p-4 rounded shadow-sm">
                      <h6 className="text-muted">Declined Item</h6>
                      <h3>
                        {formatNumber(data.dashboardData?.acceptDeclineAllItem?.decline || 0)} Unit
                      </h3>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="stat-card bg-white p-4 rounded shadow-sm">
                      <h6 className="text-muted">Accepted</h6>
                      <h3>
                        {formatNumber(data.dashboardData?.acceptDeclineAllItem?.acceptMan || 0)} People
                      </h3>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="stat-card bg-white p-4 rounded shadow-sm">
                      <h6 className="text-muted">Declined</h6>
                      <h3>
                        {formatNumber(data.dashboardData?.acceptDeclineAllItem?.declineMan || 0)} People
                      </h3>
                    </div>
                  </div>
                </div>
              )}

              {/* Charts Row */}
              <div className="row g-4 mb-4">
                <div className="col-md-8">
                  <div className="chart-card bg-white p-4 rounded shadow-sm">
                    <h5>Site Performance Trends</h5>
                    {data.dashboardData && (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          data={data.dashboardData?.dashBoardSummaryBySites || []}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="siteId" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar
                            dataKey="total"
                            fill={COLORS.primary}
                            radius={[4, 4, 0, 0]}
                          />
                          <Bar
                            dataKey="submitted"
                            fill={COLORS.success}
                            radius={[4, 4, 0, 0]}
                          />
                          <Bar
                            dataKey="pending"
                            fill={COLORS.warning}
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="chart-card bg-white p-4 rounded shadow-sm">
                    <h5>Acceptance Distribution People</h5>
                    {data.dashboardData && (
                      <ResponsiveContainer width="100%" height={400}>
                        <PieChart>
                          <Pie
                            data={preparePieData(data.dashboardData?.acceptDeclineAllItem)}
                            innerRadius={60}
                            outerRadius={150}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {preparePieData(data.dashboardData?.acceptDeclineAllItem).map(
                              (_, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                                />
                              )
                            )}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>

              {/* Comparison Charts */}
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="chart-card bg-white p-4 rounded shadow-sm">
                    <h5>BKK Year Comparison</h5>
                    {data.dashboardDataCompareBKK && (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          data={data.dashboardDataCompareBKK}
                          margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          {YEARS.map((year, index) => (
                            <Bar
                              key={year}
                              dataKey={year}
                              fill={CHART_COLORS[index % CHART_COLORS.length]}
                              radius={[4, 4, 0, 0]}
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="chart-card bg-white p-4 rounded shadow-sm">
                    <h5>LPN Year Comparison</h5>
                    {data.dashboardDataCompareLpn && (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          data={data.dashboardDataCompareLpn}
                          margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          {YEARS.map((year, index) => (
                            <Bar
                              key={year}
                              dataKey={year}
                              fill={CHART_COLORS[index % CHART_COLORS.length]}
                              radius={[4, 4, 0, 0]}
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <Footer />
      </div>
    </div>
  );
};
